import {PageSkeleton} from "../components/loading/GroupSkeleton";
// components/RestrictedAccess.tsx
import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import AuthPage from "../pages/AuthPage";

/** Props that RestrictedAccess will inject into the protected page */
export type InjectedAuthProps = {
  username: string | null;
  userEmail: string | null;
  onLogout: () => Promise<void>;
};

/** Utility: decode JWT payload if only the token string is available */
function decodeJwt(token: string): any | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

type RestrictedAccessProps<P> = {
  /** The protected page/component (NOT a JSX element) */
  page: React.ComponentType<P & InjectedAuthProps>;
  /** Props for the protected page (without the injected auth props) */
  pageProps?: P;
};

/**
 * RestrictedAccess
 * - Checks Amplify auth on mount.
 * - If unauthenticated, shows <AuthPage /> as a built-in fallback.
 * - If authenticated, renders the given page and injects { username, userEmail, onLogout }.
 */
export function RestrictedAccess<P = {}>({ page: Page, pageProps }: RestrictedAccessProps<P>) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();

        // Prefer Amplify's parsed payload; otherwise decode the JWT string
        const payload: any =
          (session.tokens?.idToken as any)?.payload ??
          (session.tokens?.idToken
            ? decodeJwt(session.tokens.idToken.toString())
            : null);

        if (payload) {
          localStorage.setItem("idToken", session.tokens!.idToken!.toString());
          const nameFromJwt =
            payload?.name ||
            payload?.given_name ||
            payload?.email ||
            payload?.["cognito:username"] ||
            null;

          setUserEmail(payload?.email ?? null);
          setUsername(nameFromJwt);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleLogout = useMemo(
    () => async () => {
      await signOut({ global: true });
      localStorage.removeItem("idToken");
      // Hard redirect to the login route to reset state cleanly
      window.location.href = "/sessions";
    },
    []
  );

  if (isLoading) {
    return (
      <PageSkeleton/>
    );
  }

  if (!isAuthenticated) {
    // Built-in fallback: render the login page
    return <AuthPage />;
  }

  // Authenticated: render protected page with injected props
  return (
    <Page
      {...(pageProps as P)}
      username={username}
      userEmail={userEmail}
      onLogout={handleLogout}
    />
  );
}
